import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import DeleteItemButton from 'analysis-tool/data-frame-creation/steps/DeleteItemButton';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import getTestProps from 'test/lib/cypress/getTestProps';

import DataFrameColumnRelationshipSelector, {
  DataFrameColumnRelationship,
} from './DataFrameColumnRelationshipSelector';

const EMPTY_RELATIONSHIP = {leftColumn: null, rightColumn: null};

export interface DataFrameColumnRelationshipMultiSelectorProps {
  leftDataFrame: DataFrameSpec;
  rightDataFrame: DataFrameSpec;
  relationships: Array<DataFrameColumnRelationship>;
  onSelectRelationships: (newRelationships: Array<DataFrameColumnRelationship>) => void;
  addItemLabel: string;
  readOnly?: boolean;
}

/**
 * Component to map relationships between two data frames' columns.
 *
 * Some notes on behavior, which may need to be validated outside this component:
 * • Relationships may be incomplete (missing a column) or empty (missing both columns).
 * • Relationships may duplicate each other.
 * • After adding at least one relationship, users cannot remove the last column.
 */
export default function DataFrameColumnRelationshipMultiSelector({
  leftDataFrame,
  rightDataFrame,
  relationships,
  onSelectRelationships,
  addItemLabel,
  readOnly,
}: DataFrameColumnRelationshipMultiSelectorProps): JSX.Element {
  // If there are no relationships, show at least one placeholder row
  const displayRelationships = relationships.length > 0 ? relationships : [EMPTY_RELATIONSHIP];

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'DataFrameColumnRelationshipMultiSelector', element: 'root'})}
    >
      {displayRelationships.map((relationship, index) => (
        <div key={index} className={css(styles.selectorWrapper)}>
          <DataFrameColumnRelationshipSelector
            relationship={relationship}
            leftDataFrame={leftDataFrame}
            rightDataFrame={rightDataFrame}
            onSelectRelationship={(newRelationship) => {
              const newRelationships = [...relationships];
              newRelationships.splice(index, 1, newRelationship);
              onSelectRelationships(newRelationships);
            }}
            readOnly={readOnly}
          />
          {relationships.length > 1 && !readOnly && (
            <DeleteItemButton
              onClick={() => {
                const newRelationships = [...relationships];
                newRelationships.splice(index, 1);
                onSelectRelationships(newRelationships);
              }}
            />
          )}
        </div>
      ))}
      {!readOnly && (
        <AddItemButton
          label={addItemLabel}
          onClick={() => {
            onSelectRelationships(
              // If the user clicks 'Add' when there is only a placeholder row, a second empty row should appear
              relationships.length === 0
                ? [EMPTY_RELATIONSHIP, EMPTY_RELATIONSHIP]
                : [...relationships, EMPTY_RELATIONSHIP]
            );
          }}
        />
      )}
    </div>
  );
}

export function ReadOnlyDataFrameColumnRelationshipMultiSelector(
  props: Pick<
    DataFrameColumnRelationshipMultiSelectorProps,
    'leftDataFrame' | 'rightDataFrame' | 'relationships'
  >
): JSX.Element {
  return (
    <DataFrameColumnRelationshipMultiSelector
      {...props}
      onSelectRelationships={noop}
      addItemLabel=""
      readOnly={true}
    />
  );
}

export const styles = StyleSheet.create({
  selectorWrapper: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 12,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});
